import { ButtonBase, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  text: string;
  variant?: 'primary' | 'secondary' | 'primaryText' | 'recruiting' | 'lead' | 'sales' | 'default' | 'error';
  underline?: 'always' | 'hover' | 'never';
  size?: 'xs' | 'sm' | 'md' | 'md+' | 'lg' | 'xl';
  textTransform?: any;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  download?: boolean;
  allowFileUpload?: boolean;
  disabled?: boolean;
  inline?: boolean;
  mt?: string;
  mb?: string;
  ml?: string;
  mr?: string;
  bold?: boolean;
  wordBreak?: any;
  noWrap?: boolean;
  IconComponent?: React.ReactNode;
  iconPlacement?: 'start' | 'end';
  showIconOnHover?: boolean;

  action?: (event: any) => any;
  handleFileUpload?(acceptedFiles: any): void;
}

const PaiLink: React.FC<Props> = ({
  text,
  variant = 'default',
  underline = 'hover',
  size = 'xs',
  textTransform,
  href,
  target,
  download,
  allowFileUpload = false,
  disabled,
  inline,
  mt,
  mb,
  ml,
  mr,
  bold,
  wordBreak,
  noWrap,
  IconComponent,
  iconPlacement = 'start',
  showIconOnHover,
  action,
  handleFileUpload,
}) => {
  const { classes, cx } = useStyles({ size, variant, underline, mt, mb, ml, mr, bold, iconPlacement, showIconOnHover });

  return (
    <>
      {!href ? (
        <ButtonBase
          className={classes.link}
          component={allowFileUpload ? 'label' : 'button'}
          onClick={(event: React.MouseEvent) => {
            if (!_.isNil(action)) {
              event.stopPropagation();
              action(event);
            } else {
              event.stopPropagation();
            }
          }}
          disableRipple={true}
          disabled={disabled}
          style={{ textTransform, wordBreak, whiteSpace: noWrap ? 'nowrap' : undefined, display: inline ? 'inline' : undefined }}
        >
          {!_.isNil(IconComponent) && iconPlacement === 'start' ? IconComponent : null}
          <Typography className={cx(classes.text, disabled ? 'disabled' : null)}>{text}</Typography>
          {!_.isNil(IconComponent) && iconPlacement === 'end' ? IconComponent : null}
          {allowFileUpload && (
            <input type={'file'} accept={'.csv, .xls, .xlsx'} multiple={true} style={{ display: 'none' }} onChange={handleFileUpload} />
          )}
        </ButtonBase>
      ) : (
        <ButtonBase
          className={classes.link}
          href={href}
          target={target}
          download={download}
          disableRipple={true}
          disabled={disabled}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
          }}
          style={{ textTransform, wordBreak, whiteSpace: noWrap ? 'nowrap' : undefined }}
        >
          {!_.isNil(IconComponent) ? IconComponent : null}
          <Typography className={cx(classes.text, disabled ? 'disabled' : null)}>{text}</Typography>
        </ButtonBase>
      )}
    </>
  );
};

const useStyles = makeStyles<{
  size?: 'xs' | 'sm' | 'md' | 'md+' | 'lg' | 'xl';
  variant: 'primary' | 'secondary' | 'primaryText' | 'recruiting' | 'lead' | 'sales' | 'default' | 'error';
  underline: 'always' | 'hover' | 'never';
  mt?: string;
  mb?: string;
  ml?: string;
  mr?: string;
  bold?: boolean;
  iconPlacement?: 'start' | 'end';
  showIconOnHover?: boolean;
}>()((theme, { size, variant, underline, mt, mb, ml, mr, bold, iconPlacement, showIconOnHover }) => ({
  link: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: mt ? mt : undefined,
    marginLeft: ml ? ml : undefined,
    marginBottom: mb ? mb : undefined,
    marginRight: mr ? mr : undefined,
    width: 'fit-content',
    whiteSpace: 'pre-wrap',
    '& svg': {
      visibility: showIconOnHover ? 'hidden' : 'visible',
      marginRight: iconPlacement === 'start' ? '8px' : undefined,
      marginLeft: iconPlacement === 'end' ? '8px' : undefined,
      fontSize:
        size === 'xs'
          ? theme.typography.pxToRem(12)
          : size === 'sm'
          ? theme.typography.pxToRem(16)
          : size === 'md'
          ? theme.typography.pxToRem(16)
          : size === 'md+'
          ? theme.typography.pxToRem(16)
          : size === 'lg'
          ? theme.typography.pxToRem(16)
          : size === 'xl'
          ? theme.typography.pxToRem(20)
          : theme.typography.pxToRem(16),
      color:
        variant === 'default'
          ? theme.palette.text.secondary
          : variant === 'primary'
          ? theme.palette.primary.main
          : variant === 'secondary'
          ? theme.palette.secondary.main
          : variant === 'primaryText'
          ? theme.palette.text.primary
          : variant === 'recruiting'
          ? theme.palette.recruiting.main
          : variant === 'lead'
          ? theme.palette.lead.main
          : variant === 'sales'
          ? theme.palette.sales.main
          : variant === 'error'
          ? theme.palette.error.main
          : theme.palette.text.secondary,
    },
    '&:hover': {
      '& svg': {
        visibility: 'visible',
        color: variant === 'default' ? theme.palette.primary.main : theme.palette.secondary.main,
      },
      '& p': {
        textDecoration: underline === 'hover' ? 'underline' : undefined,
        color: variant === 'default' ? theme.palette.primary.main : theme.palette.secondary.main,
      },
    },
  },
  text: {
    color:
      variant === 'default'
        ? theme.palette.text.secondary
        : variant === 'primary'
        ? theme.palette.primary.main
        : variant === 'secondary'
        ? theme.palette.secondary.main
        : variant === 'primaryText'
          ? theme.palette.text.primary
        : variant === 'recruiting'
        ? theme.palette.recruiting.main
        : variant === 'lead'
        ? theme.palette.lead.main
        : variant === 'sales'
        ? theme.palette.sales.main
        : variant === 'error'
        ? theme.palette.error.main
        : theme.palette.text.secondary,
    textUnderlineOffset: '4px',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontSize:
      size === 'xs'
        ? theme.typography.pxToRem(11)
        : size === 'sm'
        ? theme.typography.pxToRem(12)
        : size === 'md'
        ? theme.typography.pxToRem(13)
        : size === 'md+'
        ? theme.typography.pxToRem(14)
        : size === 'lg'
        ? theme.typography.pxToRem(15)
        : size === 'xl'
        ? theme.typography.pxToRem(18)
        : theme.typography.pxToRem(13),
    fontWeight: bold ? 'bold' : undefined,
    textDecoration: underline === 'always' ? 'underline' : underline,
    '&.disabled': {
      color: theme.palette.greys.light,
    },
  },
}));

export default PaiLink;
